"use client";

import { useStore } from "@nanostores/react";
import { atom } from "nanostores";
import { useMemo } from "react";
import { type AgentClientParam, parseAgentClientParam } from "@/agent/client/types";
import type { generationPluginClient } from "@/plugins/defaults/generation/client";

/**
 * ('generation' plugin) Reactively consume context.status.
 *
 * @param agent - AgentClient instance
 * @example
 * ```typescript
 * const status = useAgentStatus(agent); // { listening: boolean; thinking: boolean; speaking: boolean } | null
 * console.log(status);
 * console.log(status?.listening);
 * ```
 */
export const useAgentStatus = <
  Agent extends AgentClientParam<[typeof generationPluginClient]> | null,
>(
  agent: Agent,
) => {
  const typedAgent = parseAgentClientParam(agent);
  const defaultStatus = { listening: false, thinking: false, speaking: false };
  const store = useMemo(
    () => typedAgent?.generation.atoms.status().store ?? atom(defaultStatus),
    [typedAgent],
  );
  const data = useStore(store);
  return data;
};

export const useAgentMessages = <
  Agent extends AgentClientParam<[typeof generationPluginClient]> | null,
>(
  agent: Agent,
) => {
  const typedAgent = parseAgentClientParam(agent);
  const store = useMemo(
    () => typedAgent?.generation.atoms.messages().store ?? atom([]),
    [typedAgent],
  );
  const data = useStore(store);
  return data;
};
